/**
 * CalendrierPage — calendrier d'ouverture cantine / périscolaire (manager/admin).
 *
 * Le manager bascule les jours ouverts/fermés du mois. Par défaut un jour non
 * renseigné est considéré fermé (aucune réservation générée). Cliquer un jour
 * l'ouvre ; recliquer le ferme.
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarRange } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Alert, Button, Field, Input } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import type { ScolaireJourService, ServiceScolaire, JourNonTravaille } from '@roronoa-collectivites/shared-types';

const SERVICES: { value: ServiceScolaire; label: string }[] = [
  { value: 'cantine', label: 'Cantine' },
  { value: 'periscolaire', label: 'Périscolaire' },
  { value: 'alsh', label: 'ALSH' },
];
const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Formate une Date en 'YYYY-MM-DD' en HEURE LOCALE.
// (toISOString() convertit en UTC → en France minuit local = la veille en UTC,
//  ce qui décalait tout le calendrier et les réservations d'un jour.)
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function monthGrid(year: number, month: number): (string | null)[] {
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() === 0 ? 7 : first.getDay()) - 1; // lundi=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(ymd(new Date(year, month, d)));
  return cells;
}

export function CalendrierPage() {
  const [service, setService] = useState<ServiceScolaire>('cantine');
  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [jours, setJours] = useState<Record<string, ScolaireJourService>>({});
  const [nonTravailles, setNonTravailles] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Application par plage
  const [plageOpen, setPlageOpen] = useState(false);
  const [pDebut, setPDebut] = useState('');
  const [pFin, setPFin] = useState('');
  const [pJours, setPJours] = useState<number[]>([1, 2, 4, 5]); // lun mar jeu ven par défaut
  const [pOuvert, setPOuvert] = useState(true);
  const [pBusy, setPBusy] = useState(false);
  const [pMsg, setPMsg] = useState<string | null>(null);

  const cells = useMemo(() => monthGrid(cursor.y, cursor.m), [cursor]);
  const from = cells.find((c) => c) ?? ymd(new Date(cursor.y, cursor.m, 1));
  const to = [...cells].reverse().find((c) => c) ?? from;

  async function load() {
    try {
      const [list, jnt] = await Promise.all([
        api.scolaire.listJoursService(service, from, to),
        api.scolaire.planning.joursNonTravailles(from, to).catch(() => [] as JourNonTravaille[]),
      ]);
      const map: Record<string, ScolaireJourService> = {};
      for (const j of list) map[j.dateService] = j;
      setJours(map);
      setNonTravailles(new Map(jnt.map((j) => [j.date, j.libelle])));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [service, cursor]);

  function togglePJour(iso: number) {
    setPJours((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort());
  }
  async function appliquerPlage() {
    if (!pDebut || !pFin || pJours.length === 0) { setError('Renseignez une période et au moins un jour.'); return; }
    if (pFin < pDebut) { setError('La date de fin doit être après la date de début.'); return; }
    setPBusy(true); setError(null); setPMsg(null);
    try {
      const res = await api.scolaire.bulkJoursService({ serviceType: service, dateDebut: pDebut, dateFin: pFin, joursSemaine: pJours, ouvert: pOuvert });
      setPMsg(`${res.posed} jour(s) ${pOuvert ? 'ouverts' : 'fermés'} sur la période.`);
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de l\u2019application'); }
    finally { setPBusy(false); }
  }

  async function toggle(date: string) {
    const current = jours[date]?.ouvert ?? false;
    setBusy(date); setError(null);
    try {
      const updated = await api.scolaire.setJourService({ serviceType: service, dateService: date, ouvert: !current });
      setJours((prev) => ({ ...prev, [date]: updated }));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur lors de la mise à jour');
    } finally { setBusy(null); }
  }

  const moisLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <ScolaireLayout activeTab="calendrier">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Calendrier d'ouverture</h1>
          <p className="mt-1 text-sm text-ink-muted">Cliquez un jour pour l'ouvrir ou le fermer. Un jour fermé ne génère aucune réservation.</p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {SERVICES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setService(s.value)}
                className={[
                  'inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all',
                  service === s.value ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70 hover:text-ink',
                ].join(' ')}
              >
                {s.label}
              </button>
            ))}
          </div>
          <Button variant="secondary" size="sm" className="shrink-0" onClick={() => setPlageOpen((v) => !v)}>
            <CalendarRange className="h-4 w-4" /> Appliquer une plage
          </Button>
        </div>

        {plageOpen && (
          <Card className="mb-4">
            <CardBody>
              <h2 className="mb-1 font-display text-base font-semibold text-ink">Ouvrir/fermer une période d'un coup</h2>
              <p className="mb-3 text-sm text-ink-muted">
                Choisissez une période et les jours d'ouverture. Évite de cliquer chaque jour à la main —
                les week-ends (ou le mercredi selon l'école) restent fermés en ne les cochant pas.
              </p>
              {pMsg && <Alert variant="success" className="mb-3">{pMsg}</Alert>}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Du" htmlFor="pl-debut" required><Input id="pl-debut" type="date" value={pDebut} onChange={(e) => setPDebut(e.target.value)} /></Field>
                <Field label="Au" htmlFor="pl-fin" required><Input id="pl-fin" type="date" value={pFin} onChange={(e) => setPFin(e.target.value)} /></Field>
              </div>
              <div className="mt-3">
                <span className="mb-1.5 block text-sm font-medium text-ink">Jours d'ouverture</span>
                <div className="flex flex-wrap gap-1.5">
                  {[{ iso: 1, l: 'Lun' }, { iso: 2, l: 'Mar' }, { iso: 3, l: 'Mer' }, { iso: 4, l: 'Jeu' }, { iso: 5, l: 'Ven' }, { iso: 6, l: 'Sam' }, { iso: 7, l: 'Dim' }].map((j) => {
                    const on = pJours.includes(j.iso);
                    return (
                      <button key={j.iso} type="button" onClick={() => togglePJour(j.iso)}
                        className={['inline-flex h-9 items-center rounded-r-pill px-3.5 text-sm font-medium transition-all',
                          on ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                        {j.l}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => setPOuvert(true)}
                    className={['inline-flex h-9 items-center rounded-r-pill px-3.5 text-sm font-medium transition-all', pOuvert ? 'bg-cantine text-white' : 'border border-border text-ink-muted'].join(' ')}>Ouvrir</button>
                  <button type="button" onClick={() => setPOuvert(false)}
                    className={['inline-flex h-9 items-center rounded-r-pill px-3.5 text-sm font-medium transition-all', !pOuvert ? 'bg-cantine text-white' : 'border border-border text-ink-muted'].join(' ')}>Fermer</button>
                </div>
                <Button variant="cantine" loading={pBusy} onClick={appliquerPlage} disabled={!pDebut || !pFin || pJours.length === 0}>Appliquer</Button>
              </div>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody>
            <div className="mb-4 flex items-center justify-between">
              <button type="button" onClick={() => setCursor((c) => ({ y: c.m === 0 ? c.y - 1 : c.y, m: c.m === 0 ? 11 : c.m - 1 }))}
                className="inline-flex h-9 w-9 items-center justify-center rounded-r-md border border-border text-ink-muted hover:bg-surface/60" aria-label="Mois précédent">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-display text-base font-semibold capitalize text-ink">{moisLabel}</span>
              <button type="button" onClick={() => setCursor((c) => ({ y: c.m === 11 ? c.y + 1 : c.y, m: c.m === 11 ? 0 : c.m + 1 }))}
                className="inline-flex h-9 w-9 items-center justify-center rounded-r-md border border-border text-ink-muted hover:bg-surface/60" aria-label="Mois suivant">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1.5">
              {JOURS.map((j) => <div key={j} className="text-center text-xs font-medium text-ink-subtle">{j}</div>)}
              {cells.map((date, i) => {
                if (!date) return <div key={`e-${i}`} />;
                const day = Number(date.slice(8, 10));
                const ouvert = jours[date]?.ouvert ?? false;
                const libelleNT = nonTravailles.get(date);
                const estVacances = libelleNT?.startsWith('Vacances');
                const estFerie = libelleNT && !estVacances;
                const bgNT = ouvert
                  ? 'border-cantine/40 bg-cantine/15 text-cantine-fg'
                  : estVacances
                    ? 'border-amber-400/50 bg-amber-400/15 text-amber-200'
                    : estFerie
                      ? 'border-violet-400/50 bg-violet-400/15 text-violet-200'
                      : 'border-border bg-surface/30 text-ink-subtle hover:bg-surface/60';
                return (
                  <button
                    key={date}
                    type="button"
                    disabled={busy === date}
                    onClick={() => toggle(date)}
                    className={['relative aspect-square rounded-r-md border text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine', bgNT].join(' ')}
                    title={libelleNT ? `${libelleNT} — ${ouvert ? 'ouvert' : 'fermé'}` : (ouvert ? 'Ouvert — cliquer pour fermer' : 'Fermé — cliquer pour ouvrir')}
                  >
                    {day}
                    {libelleNT && <span className="absolute bottom-0.5 left-0 right-0 text-center text-[8px] leading-none">{estVacances ? '🏖' : '⚑'}</span>}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-ink-muted">
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-cantine/40 bg-cantine/15" /> Ouvert</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-border bg-surface/30" /> Fermé</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-amber-400/50 bg-amber-400/15" /> 🏖 Vacances</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-violet-400/50 bg-violet-400/15" /> ⚑ Férié</span>
            </div>
          </CardBody>
        </Card>
      </main>
    </ScolaireLayout>
  );
}
